import { FC, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import noop from 'lodash/noop';
import { Game } from '../games.model';
import { TileRowCollection as TileRowCollectionType } from '../GameCard/GameCard.types';
import { useBreakpointInfo, MediaBreakpoint } from '../../../hooks/useDeviceInfo';
import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { Button } from '../../shared/Button';
import { Icon } from '../../shared/Icon';
import { Heading } from '../../shared/Heading';
import { Carousel } from '../Carousel';
import { joinStrings } from '../../../utils/string';
import { setSelectedCategory } from '../../../state/app/reducers';
import { selectSelectedCategory } from '../../../state/app/selectors';
import { useMobileSlideCreator } from './MobileSlideCreator';
import { createGamesById, createGameFinder, filterCollectionsWithContent } from './utils';
import { ViewAllGamesGrid } from './ViewAllGamesGrid';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { TileRowSection } from './TileRowSection';
import { toggleCategoryNavigation } from '../helper';
import './styles';

interface GamesCarouselGridProps {
  tileRowCollections: TileRowCollectionType[];
  games: Game[];
  disabled?: boolean;
  showGameName?: boolean;
  fallbackImageAltText?: string;
  fallbackImageUrl?: string;
  onViewGameInfo?: (game: Game) => void;
  onPlayDemo?: (game: Game) => void;
  onPlayGame?: (game: Game) => void;
  showArrows?: boolean;
  showDots?: boolean;
  autoplay?: boolean;
  autoplayInterval?: number;
  title?: string;
  categorySlug: string;
  categoryInternalName: string;
  category?: string;
}

const VISIBLE_CONFIG = Object.freeze({
  wide: 1,
  desktop: 1,
  tablet: 1,
  mobile: 1,
});

export const GamesCarouselGrid: FC<GamesCarouselGridProps> = ({
  tileRowCollections,
  games,
  disabled = false,
  showGameName = true,
  fallbackImageAltText = '',
  fallbackImageUrl = '',
  onViewGameInfo = noop,
  onPlayDemo = noop,
  onPlayGame = noop,
  showArrows = true,
  showDots = false,
  autoplay = false,
  autoplayInterval = 5000,
  title,
  categorySlug,
  categoryInternalName,
  category,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedCategory = useSelector(selectSelectedCategory);
  const { t } = useTranslations();
  const shouldUseMobileLayout = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]); // Below 1024px
  const gamesById = useMemo(() => createGamesById(games), [games]);
  const findGameById = useCallback(createGameFinder(gamesById), [gamesById]);

  const collectionsWithContent = useMemo(() => {
    return filterCollectionsWithContent(tileRowCollections, findGameById);
  }, [tileRowCollections, findGameById]);

  const hasViewAllConfig = collectionsWithContent.some((config) => config.SeeMoreViewAll);
  const viewAllTarget = useMemo(
    () => title || collectionsWithContent[0]?.internal_name || '',
    [title, collectionsWithContent]
  );
  const canShowViewAll = hasViewAllConfig && Boolean(viewAllTarget);
  const isGridMode = Boolean(viewAllTarget) && selectedCategory === viewAllTarget;

  const handleViewAll = useCallback((): void => {
    if (!viewAllTarget) return;

    if (categorySlug && category) {
      toggleCategoryNavigation(
        {
          gridMode: isGridMode,
          title: viewAllTarget,
          category,
          categorySlug,
        },
        {
          setGridMode: () => {}, // This is handled by the navigation
          setSelectedCategory,
        },
        { dispatch, navigate }
      );
    } else {
      // Fallback to the original behavior if navigation props are not available
      if (isGridMode) {
        dispatch(setSelectedCategory(''));
      } else {
        dispatch(setSelectedCategory(viewAllTarget));
        window.scrollTo(0, 0);
      }
    }
  }, [viewAllTarget, categorySlug, category, isGridMode, dispatch, navigate]);

  const sharedTileRowProps = useMemo(
    () => ({
      disabled,
      showGameName,
      fallbackImageAltText,
      fallbackImageUrl,
      onViewGameInfo,
      onPlayDemo,
      onPlayGame,
    }),
    [disabled, showGameName, fallbackImageAltText, fallbackImageUrl, onViewGameInfo, onPlayDemo, onPlayGame]
  );

  const { createMobileSlides } = useMobileSlideCreator({
    collectionsWithContent,
    findGameById,
    containerWidth,
    disabled,
    showGameName,
    fallbackImageAltText,
    fallbackImageUrl,
    onViewGameInfo,
    onPlayDemo,
    onPlayGame,
  });

  const desktopSlides = useMemo(() => {
    return collectionsWithContent.map((config, index) => (
      <TileRowSection
        key={config.id || index}
        config={config}
        findGameById={findGameById}
        containerElementWidth={containerWidth}
        mode="carousel"
        {...sharedTileRowProps}
      />
    ));
  }, [collectionsWithContent, findGameById, containerWidth, sharedTileRowProps]);

  const slides = useMemo(() => {
    return shouldUseMobileLayout ? createMobileSlides() : desktopSlides;
  }, [shouldUseMobileLayout, createMobileSlides, desktopSlides]);

  if (slides.length === 0) {
    return null;
  }

  const visibleConfig = VISIBLE_CONFIG;

  return (
    <div
      className={joinStrings(['games-carousel-grid', 'list-component', isGridMode && 'games-carousel-grid--grid-mode'])}
      ref={containerRef}
    >
      {(title || canShowViewAll) && (
        <div className="carousel-tile-row carousel-tile-row--static-header">
          <div className="carousel-tile-row__header">
            {title && (
              <Heading level={4} className="carousel-tile-row__title">
                {title}
              </Heading>
            )}
            {canShowViewAll && (
              <Button type="button" variant="text" onClick={handleViewAll} className="button--text">
                {isGridMode ? t('game.carousel.back-to-lobby') : t('game.carousel.view-all')}
                <Icon variant={isGridMode ? 'ChevronLeft' : 'ChevronRight'} />
              </Button>
            )}
          </div>
        </div>
      )}

      {isGridMode ? (
        <ViewAllGamesGrid
          categoryName={viewAllTarget}
          isVisible={isGridMode}
          onBackToCarousel={(): void => {
            dispatch(setSelectedCategory(''));
          }}
          disabled={disabled}
          showGameName={showGameName}
          fallbackImageAltText={fallbackImageAltText}
          fallbackImageUrl={fallbackImageUrl}
          onViewGameInfo={onViewGameInfo}
          onPlayDemo={onPlayDemo}
          onPlayGame={onPlayGame}
          containerElementWidth={containerWidth}
          displayMode="grid"
          originalTileRowCollections={collectionsWithContent}
          originalGames={games}
          categoryInternalName={categoryInternalName}
        />
      ) : (
        <Carousel
          disableAllInteractions={true}
          slides={slides}
          autoplay={autoplay}
          autoplayInterval={autoplayInterval}
          visibleConfig={visibleConfig}
          adjustSlidesOnResize
          carouselStyle="inside"
          disableSlideContent={disabled}
        />
      )}
    </div>
  );
};
